import axios from '../../../lib/axios';
import { useEffect, useRef, useState } from 'react';
import { SuppliesMainGroupName } from '../../../models/suppliesMain';
import { generateImageFileNameWithRandomName } from '../../../utils/generateImageFileName';
import { useTranslation } from 'react-i18next';
import { IMAGE_CATEGORY_ROOT_URL } from '../../../constants/imageUrl';
import { MASTER_MAIN_GROUP_CREATE, MASTER_MAIN_GROUP_UPDATE } from '../../../lib/endpoints/category';

interface Props {
  onClose: VoidFunction;
  onfetchData: VoidFunction;
  selectedMain: SuppliesMainGroupName;
  onSetSelectedMain: Function;
}

export const useFormDialogMainController = ({
  selectedMain,
  onSetSelectedMain,
  onfetchData,
  onClose,
}: Props) => {
  const { t } = useTranslation('global');

  const [imageToShow, setImageToShow] = useState<any>(null!);
  const [imageFile, setImageFile] = useState<any>(null!);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [loadingConfirmButton, setLoadingConfirmButton] = useState<boolean>(false);
  const [imageError, setImageError] = useState<string>(null!);
  const [errorMessage, setErrorMessage] = useState<any>('');
  const mgCode = useRef<HTMLInputElement>(null!);
  const nameLa = useRef<HTMLInputElement>(null!);
  const nameEn = useRef<HTMLInputElement>(null!);

  const handleValidateForm = (): boolean => {
    if (!selectedMain && !imageFile) {
      setImageError('Please select an image.');
      return false;
    }
    if (imageFile && imageError) {
      setImageError('');
    }
    setErrorMessage('');
    return true;
  };

  const handleReset = () => {
    onSetSelectedMain(null);
    setImageToShow(null);
    setImageFile(null);
    mgCode.current.value = '';
    nameLa.current.value = '';
    nameEn.current.value = '';
  };

  const handleSelectImage = (e: any) => {
    const file = e.target.files[0];
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        setImageToShow(reader.result);
      }
    };
    reader.readAsDataURL(file);
    setImageToShow(file);
  };

  //-----------> add main category
  const handleAddData = async () => {
    try {
      setLoadingConfirmButton(true);
      const formData = {
        code: mgCode.current?.value,
        main_group_name_la: nameLa.current?.value,
        main_group_name_en: nameEn.current?.value,
        file: generateImageFileNameWithRandomName(imageFile),
      };
      await axios.post(`${MASTER_MAIN_GROUP_CREATE}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onClose();
      handleReset();
      await onfetchData();
    } catch (err: any) {
      try {
        const getErrorMessage: string = err.response.data.message;
        setErrorMessage(getErrorMessage);
        // console.log(err);
      } catch (err2) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirm(false);
    }
  };

  //----------------> edit main category
  const handleEditData = async () => {
    try {
      setLoadingConfirmButton(true);
      const formData: any = {
        main_group_name_la: nameLa.current?.value,
        main_group_name_en: nameEn.current?.value,
      };
      if (imageFile) {
        formData.file = generateImageFileNameWithRandomName(imageFile);
      }
      if (mgCode.current?.value === null) {
        formData.code = mgCode.current.value;
      }
      await axios.patch(`${MASTER_MAIN_GROUP_UPDATE + selectedMain.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: 'multipart/form-data',
        },
      });

      onClose();
      handleReset();
      await onfetchData();
    } catch (err: any) {
      try {
        const getErrorMessage: string = err.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err2) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirm(false);
    }
  };

  // submit function
  const handleSubmit = async () => {
    if (selectedMain?.id) {
      handleEditData();
    } else {
      handleAddData();
    }
  };

  // Open Form submit
  const handleOpenForm = (e: any) => {
    e.preventDefault();
    if (handleValidateForm()) {
      setOpenConfirm(!openConfirm);
    }
  };

  // Close Form submit
  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  useEffect(() => {
    if (selectedMain) {
      mgCode.current.value = selectedMain?.code;
      nameLa.current.value = selectedMain?.main_group_name_la;
      nameEn.current.value = selectedMain?.main_group_name_en;
      setImageToShow(IMAGE_CATEGORY_ROOT_URL + selectedMain?.icon?.url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMain]);

  return {
    handleSelectImage,
    imageToShow,
    mgCode,
    nameLa,
    nameEn,
    handleSubmit,
    imageError,
    setImageToShow,
    openConfirm,
    handleOpenForm,
    handleCloseConfirm,
    loadingConfirmButton,
    errorMessage,
    handleReset,
  };
};
